import { useQuery } from "@tanstack/react-query";
import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo } from "react";
import { getBuildingsAsync } from "src/api/building/building-api";
import { getContractApply } from "src/api/contract/contract-api";
import { useApiOperation } from "src/api/hooks";
import { getQuestionDetailListAsync, getQuestionListAsync } from "src/api/question/qeustion-api";
import { QuestionAnswerModel, QuestionListModel, QuestionQueryParams } from "src/api/question/question-type";
import { useErrorModal } from "src/recoil/errorModal/hook";
import { numberToStringWithComma, YmdFormat } from "src/utils";

// 상담관리 상태
const QuestionStatusMap = {
  STATUS_UNRECOGNIZED: "정의되지 않은 상태",
  QUESTION_WAIT: "접수",
  QUESTION_OPEN: "접수",
  QUESTION_PROCEEDING: "처리중",
  QUESTION_HOLD: "보류",
  QUESTION_COMPLETE: "완료",
  QUESTION_CANCELED: "취소",
} as const;

type QuestionStatusMapType = (typeof QuestionStatusMap)[keyof typeof QuestionStatusMap];

const SiteMap = {
  SITE_HOMEPAGE: "홈페이지",
  SITE_EXTERNAL: "외부 플랫폼",
  SITE_ETC: "기타",
  SITE_TAAP_SPACE: "TaapSpace",
  SITE_TAAP: "Taap",
  SITE_UNRECOGNIZED: "-",
  SITE_COURT_337: "코트337",
  SITE_PNPT: "PNPT",
  SITE_NAVER: "네이버",
  "": "-",
} as const;

type SiteMapType = (typeof SiteMap)[keyof typeof SiteMap];

export interface CsItem extends QuestionListModel {
  buildingName?: string;
  answerList?: QuestionAnswerModel[];
  mbOrganizationName: string;
  durationTime?: number;
  contractName: string;
}

export interface AIReportItem extends Omit<CsItem, "status" | "site" | "answerList" | "durationTime" | "cost" | "expense" | "floorNum"> {
  status: QuestionStatusMapType | "-";
  site: SiteMapType | "-";
  answerList: string;
  cost: string;
  expense: string;
  durationTime: string | number;
  floorNum: string;
}

interface useCsDataOptions {
  noLoading?: boolean;
  richDetail?: boolean;
  questionQueryParams?: QuestionQueryParams;
  enabled?: boolean;
}

const useCsData = (options: useCsDataOptions = {}) => {
  const { noLoading = false, richDetail = true, questionQueryParams, enabled = true } = options;
  const { openErrorModal } = useErrorModal();
  // 상담관리 기본정보 목록 api
  const { executeAsync: getQuestionList } = useApiOperation(getQuestionListAsync, { noLoading: noLoading, noErrorModal: true });

  // 상담관리 상세정보 목록 api
  const { executeAsync: execGetQuestionDetailList } = useApiOperation(getQuestionDetailListAsync, {
    noLoading: noLoading,
    noErrorModal: true,
  });

  const handleError = useCallback(
    (error: any) => {
      // 에러
      let errorCode = "";
      let statusCode = 0;

      if (error?.response?.status) {
        statusCode = error?.response?.status;
      }
      if (error?.response?.data?.meta?.errorCode) {
        errorCode = error?.response?.data?.meta?.errorCode;
      }

      openErrorModal({ errorCode, statusCode, cause: error });
    },
    [openErrorModal],
  );

  // 건물 목록 api
  const { executeAsync: execGetBuildings } = useApiOperation(getBuildingsAsync, { noLoading: noLoading, noErrorModal: true });

  // 계약 목록 api
  const { executeAsync: execGetContractApply } = useApiOperation(getContractApply, { noLoading: noLoading, noErrorModal: true });

  const {
    data: questionList,
    error: questionListError,
    isFetching,
  } = useQuery({
    queryKey: ["QuestionList", questionQueryParams],
    queryFn: () => getQuestionList(questionQueryParams),
    placeholderData: (previousData) => previousData,
    select: (data) => data.data,
    enabled,
  });

  /** 상세 정보 */
  const questionIds = useMemo(() => {
    if (!questionList?.data?.content) return [];
    return questionList.data.content.map((item) => item.id);
  }, [questionList]);

  const { data: questionListDetails, error: questionDetailError } = useQuery({
    queryKey: ["QuestionListDetails", questionIds],
    queryFn: () => execGetQuestionDetailList({ page: 0, size: questionIds.length, ids: questionIds.join(",") }),
    enabled: richDetail && questionIds.length > 0,
    select: (res) => res.data.data.content,
  });

  /** 빌딩 정보 */

  const buildingIds = useMemo(() => {
    if (!questionList?.data?.content) return [];
    return [...new Set(questionList.data.content.map((item) => item.buildingId).filter(Boolean))];
  }, [questionList]);

  const { data: buildingList, error: buildingError } = useQuery({
    queryKey: ["Buildings", buildingIds],
    queryFn: () =>
      execGetBuildings({
        page: 0,
        size: buildingIds.length,
        id: buildingIds.join(","),
      }),
    enabled: richDetail && buildingIds.length > 0,
    select: (res) => res.data.data.content,
  });

  /** 계약 정보 */

  const contractIds = useMemo(() => {
    if (!questionList?.data?.content) return [];

    return questionList.data.content.reduce<string[]>((acc, item) => {
      const connectedId = item.connectedInfoList?.[0]?.connectedId;
      if (connectedId && !acc.includes(connectedId)) {
        acc.push(connectedId);
      }
      return acc;
    }, []);
  }, [questionList]);

  const { data: contractList, error: contractError } = useQuery({
    queryKey: ["Contracts", contractIds],
    queryFn: () => execGetContractApply({ page: 0, size: contractIds.length, contractIds: contractIds.join(",") }),
    enabled: richDetail && contractIds.length > 0,
    select: (res) => res.data.data.content,
  });

  useEffect(() => {
    if (questionListError) handleError(questionListError);
    if (questionDetailError) handleError(questionDetailError);
    if (buildingError) handleError(buildingError);
    if (contractError) handleError(contractError);
  }, [questionListError, questionDetailError, buildingError, contractError, handleError]);

  /**
   * 소요시간 계산
   * @param csItem
   * @returns 1시간 1분 1초 형식으로 리턴
   */
  const getDurationTime = (csItem: QuestionListModel): number | undefined => {
    // 취소 상태일때 하이픈 반환
    // 완료 상태일때 완료 날짜와 생성 날짜 차이 계산
    // 나머지 상태는 생성 날짜와 현재 시간 차이 계산

    const status = csItem.status;
    const createdMoment = moment(csItem.createdDate);
    const completedMoment = moment(csItem.completedDate);
    const nowMoment = moment();

    // 취소 상태면 하이픈 반환
    if (status === "QUESTION_CANCELED") {
      return undefined;
    }

    // 완료 상태일 때 - 생성 날짜와 완료 날짜 차이 계산
    if (status === "QUESTION_COMPLETE" && csItem.completedDate) {
      const diffMinutes = completedMoment.diff(createdMoment, "minutes");
      return diffMinutes;
    }

    // 나머지 상태일 때 - 생성 날짜와 현재 시간 차이 계산
    const diffMinutes = nowMoment.diff(createdMoment, "minutes");
    return diffMinutes;
  };

  /** 결합된 상세 데이터 */
  const fillDetailCsInfo = useCallback(
    (csItem: QuestionListModel): CsItem => {
      const buildingName = buildingList?.find((building) => String(csItem.buildingId) === String(building.id))?.buildingName;
      const answerList = questionListDetails?.find((csDetail) => String(csItem.id) === String(csDetail.id))?.answerList;
      const contract = contractList?.find((contract) => String(csItem.connectedInfoList?.[0]?.connectedId) === String(contract.contractId));

      const mbOrganizationName = contract?.mbOrganizationName || "-";
      const contractName = contract?.contractName || contract?.spaceProductName || "-";
      const durationTime = getDurationTime(csItem);

      return {
        ...csItem,
        buildingName,
        answerList,
        mbOrganizationName,
        durationTime,
        contractName,
      };
    },
    [buildingList, questionListDetails, contractList],
  );

  const detailedQuestionList = useMemo(() => {
    if (!questionList?.data?.content) {
      return [];
    }

    return questionList.data.content.map(fillDetailCsInfo);
  }, [questionList, fillDetailCsInfo]);

  const buildAIReportSource = (data: CsItem[]): AIReportItem[] => {
    return data.map((item) => {
      // 가장 최근 답변을 리턴
      const answer = item.answerList?.sort((a: QuestionAnswerModel, b: QuestionAnswerModel) => Number(b.id) - Number(a.id))?.[0].description;

      const formatData: AIReportItem = {
        ...item,
        id: item.id ?? "-",
        status: QuestionStatusMap[item.status] ?? "-",
        csCategoryType: item.csCategoryType ?? "-",
        site: SiteMap[item.site] ?? "-",
        csTypeName: item.csTypeName ?? "-",
        summary: item.summary ?? "-",
        description: item.description ?? "-",
        answerList: answer ?? "-",
        buildingName: item.buildingName ?? "-",
        assigneeEmail: item.assigneeEmail ?? "-",
        cost: item.cost ? numberToStringWithComma(item.cost) : "-",
        expense: item.expense ? numberToStringWithComma(item.expense) : "-",
        modifiedDate: item.modifiedDate ? moment(item.modifiedDate).format(YmdFormat.YYYY_MM_DD_HH_MM) : "-",
        modifiedBy: isNaN(Number(item.modifiedBy)) ? item.modifiedBy || "-" : item?.reporterEmail || "-",
        completedBy: item.completedBy ?? "-",
        createdDate: item.createdDate ? moment(item.createdDate).format(YmdFormat.YYYY_MM_DD_HH_MM) : "-",
        completedDate: item.completedDate ? moment(item.completedDate).format(YmdFormat.YYYY_MM_DD_HH_MM) : "-",
        reporterName: item.reporterName ?? "-",
        durationTime: item.durationTime ?? "-",
        mbOrganizationName: item.mbOrganizationName ?? "-",
        contractName: item.contractName ?? "-",
        floorNum: item?.floorNum ? (item?.floorNum > 0 ? `지상${item?.floorNum}층` : `지하${Math.abs(item?.floorNum)}층`) : "-",
      };

      return formatData;
    });
  };

  const getAllListForExcelDownload = useCallback(async () => {
    const unpagedParams = { ...questionQueryParams };
    delete unpagedParams.page;
    delete unpagedParams.size;

    const response = await getQuestionList(unpagedParams);
    const content = response.data.data.content;

    if (!content || content.length === 0) return response;

    const ids = content.map((item) => item.id);
    const buildingIdSet = [...new Set(content.map((item) => item.buildingId).filter(Boolean))] as string[];
    const contractIdSet = content.reduce<string[]>((acc, item) => {
      const connectedId = item.connectedInfoList?.[0]?.connectedId;
      if (connectedId && !acc.includes(connectedId)) {
        acc.push(connectedId);
      }
      return acc;
    }, []);

    const CHUNK_SIZE = 300;

    const detailChunks = _.chunk(ids, CHUNK_SIZE);
    const buildingChunks = _.chunk(buildingIdSet, CHUNK_SIZE);
    const contractChunks = _.chunk(contractIdSet, CHUNK_SIZE);

    const [detailResults, buildingResults, contractResults] = await Promise.all([
      Promise.all(detailChunks.map((chunk) => execGetQuestionDetailList({ page: 0, size: chunk.length, ids: chunk.join(",") }))),
      Promise.all(buildingChunks.map((chunk) => execGetBuildings({ page: 0, size: chunk.length, id: chunk.join(",") }))),
      Promise.all(contractChunks.map((chunk) => execGetContractApply({ page: 0, size: chunk.length, contractIds: chunk.join(",") }))),
    ]);

    const detailList = detailResults.flatMap((res) => res?.data?.data?.content ?? []);
    const buildings = buildingResults.flatMap((res) => res?.data?.data?.content ?? []);
    const contracts = contractResults.flatMap((res) => res?.data?.data?.content ?? []);

    response.data.data.content = content.map((csItem) => {
      const buildingName = buildings.find((b: any) => String(csItem.buildingId) === String(b.id))?.buildingName;
      const answerList = detailList.find((d: any) => String(csItem.id) === String(d.id))?.answerList;
      const contract = contracts.find((c: any) => String(csItem.connectedInfoList?.[0]?.connectedId) === String(c.contractId));
      const mbOrganizationName = contract?.mbOrganizationName || "-";
      const contractName = contract?.contractName || contract?.spaceProductName || "-";
      const durationTime = getDurationTime(csItem);

      return {
        ...csItem,
        buildingName,
        answerList,
        mbOrganizationName,
        durationTime,
        contractName,
      };
    });

    return response;
  }, [getQuestionList, questionQueryParams, execGetQuestionDetailList, execGetBuildings, execGetContractApply]);

  return {
    questionList: questionList?.data.content ?? [],
    detailedQuestionList,
    buildAIReportSource,
    questionListMeta: questionList?.meta,
    isFetching,
    getAllListForExcelDownload,
  };
};

export default useCsData;
